// src/pages/RideRequests.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { MessageCircle, CheckCircle, XCircle } from "lucide-react";
import socket from "../socket";

const API_WRK = process.env.REACT_APP_API_WRK;
const API_BASE = API_WRK?.replace("/api/worker", "");
const API_RIDEPAY = `${API_BASE}/api/ridepayment`;

const RideRequests = () => {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [chats, setChats] = useState([]);
  const [rideDetails, setRideDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const workerId = localStorage.getItem("workerId");

  const [modal, setModal] = useState({
    open: false,
    type: null,
    request: null,
  });

  /* ================================
      Helpers
  ================================= */
  const getRawStatus = (req) =>
    req.requestStatus || req.rideStatus || req.status || "pending";

  const getStatusLabel = (raw) => {
    const map = {
      worker_completed: "completed",
      customer_completed: "completed",
      fully_completed: "completed",
    };
    return map[raw] || raw;
  };

  const safeFormatDateTime = (dateString, fallback = "N/A") => {
    if (!dateString) return fallback;
    try {
      return format(parseISO(dateString), "MMMM d, yyyy");
    } catch {
      return fallback;
    }
  };

  const getFullPhotoUrl = (raw) => {
    if (!raw) return null;
    if (raw.startsWith("http")) return raw;
    return `${API_BASE}${raw.startsWith("/") ? raw : "/" + raw}`;
  };

  const getStatusBadge = (status) => {
    if (!status) return null;

    const colors = {
      pending: "bg-yellow-400",
      accepted: "bg-green-500",
      declined: "bg-red-500",
      completed: "bg-blue-500",
      worker_completed: "bg-blue-500",
      customer_completed: "bg-blue-500",
      fully_completed: "bg-blue-500",
      refunded: "bg-purple-500",
      disputed: "bg-orange-500",
    };

    return (
      <div className="absolute top-4 right-16 flex items-center text-xs font-semibold text-gray-700 bg-white/80 px-2 py-1 rounded-full shadow-sm">
        <span
          className={`w-2 h-2 rounded-full mr-1 ${
            colors[status] || "bg-gray-400"
          }`}
        ></span>
        <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
      </div>
    );
  };

  /* ================================
      Fetch Data
  ================================= */
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [reqRes, rideRes] = await Promise.all([
          axios.get(`${API_BASE}/api/ride/${rideId}/requests-and-chats`),
          axios.get(`${API_BASE}/api/ride/preview/${rideId}`),
        ]);

        const allRequests = reqRes.data.requests || [];
        const fetchedChats = reqRes.data.chatParticipants || [];

        // 🔵 This page only shows "pending" requests
        const pending = allRequests
          .filter(
            (r) => (r.requestStatus || r.status || "pending") === "pending"
          )
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() -
              new Date(a.createdAt).getTime()
          );

        setRequests(pending);
        setChats(fetchedChats);
        setRideDetails(rideRes.data);
      } catch (err) {
        console.error("❌ Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();

    socket.emit("join-ride", rideId);

    // Chat preview
    socket.on("ride-message", (data) => {
      if (String(data.rideId) !== String(rideId)) return;

      setChats((prev) => {
        const idx = prev.findIndex(
          (c) => String(c.customerId) === String(data.message.senderId)
        );

        if (idx >= 0) {
          const updated = [...prev];
          updated[idx].lastMessage = data.message.text;
          updated[idx].timestamp = new Date();
          return updated;
        }

        return [
          ...prev,
          {
            customerId: data.message.senderId,
            name: data.message.senderName || "Passenger",
            lastMessage: data.message.text,
            timestamp: new Date(),
            profilePhotoUrl: data.message.senderPhotoUrl || null,
          },
        ];
      });
    });

    // Status updates → if a pending request becomes accepted/declined
    // we simply remove it from this list
    socket.on("ride-status-update", (update) => {
      if (String(update.rideId) !== String(rideId)) return;

      setRequests((prev) =>
        prev.filter(
          (r) => String(r._id) !== String(update.requestId) // drop it, it moves to "Your Riders"
        )
      );
    });

    // New incoming request → add to list
    socket.on("ride-new-request", (newReq) => {
      if (String(newReq.rideId) !== String(rideId)) return;
      const rawStatus =
        newReq.requestStatus || newReq.status || newReq.rideStatus || "pending";
      if (rawStatus !== "pending") return;
      setRequests((prev) => [newReq, ...prev]);
    });

    return () => {
      socket.off("ride-message");
      socket.off("ride-status-update");
      socket.off("ride-new-request");
    };
  }, [rideId]);

  /* ================================
      Approve / Decline
  ================================= */
  const confirmAccept = async (req) => {
    try {
      await axios.post(`${API_RIDEPAY}/approve-request`, {
        requestId: req._id,
        rideId,
      });

      socket.emit("ride-status-update", {
        rideId,
        requestId: req._id,
        status: "accepted",
      });

      // remove from pending list → goes to "Your Riders"
      setRequests((prev) =>
        prev.filter((r) => String(r._id) !== String(req._id))
      );

      setModal({ open: false, type: null, request: null });
    } catch (err) {
      console.error("❌ Accept error:", err);
      alert("Failed to accept request");
    }
  };

  const confirmDecline = async (req) => {
    try {
      await axios.post(`${API_RIDEPAY}/decline-request`, {
        requestId: req._id,
      });

      socket.emit("ride-status-update", {
        rideId,
        requestId: req._id,
        status: "declined",
      });

      // remove from pending list
      setRequests((prev) =>
        prev.filter((r) => String(r._id) !== String(req._id))
      );

      setModal({ open: false, type: null, request: null });
    } catch (err) {
      console.error("❌ Decline error:", err);
      alert("Failed to decline request");
    }
  };

  /* ================================
      Modal Controls
  ================================= */
  const openModal = (type, request) => setModal({ open: true, type, request });
  const closeModal = () =>
    setModal({ open: false, type: null, request: null });

  if (loading) return <div className="p-6">Loading requests...</div>;

  /* ================================
      UI
  ================================= */
  return (
    <>
      <div className="p-6 max-w-6xl mx-auto font-[Poppins]">
        <h2 className="text-4xl font-extrabold mb-10 text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 drop-shadow-md">
          Passenger Requests & Chats
        </h2>

        {rideDetails && (
          <div className="backdrop-blur-lg bg-white/70 shadow-xl rounded-2xl p-6 mb-10 border border-white/30">
            <p>
              <strong>Route:</strong> {rideDetails.from} → {rideDetails.to}
            </p>
            <p>
              <strong>Date:</strong>{" "}
              {safeFormatDateTime(rideDetails.date)} at {rideDetails.time}
            </p>
            <p>
              <strong>Seats Left:</strong> {rideDetails.seatsAvailable}
            </p>
          </div>
        )}

        {/* ================================
            REQUESTS LIST
        ================================= */}
        <h3 className="text-2xl font-semibold mb-6">Ride Requests</h3>

        {requests.length === 0 ? (
          <p className="text-gray-600 mb-14">
            No pending requests. Accepted riders will appear in{" "}
            <strong>Your Riders</strong>.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-8 mb-14">
            {requests.map((req) => {
              const rawStatus = getRawStatus(req);
              const status = getStatusLabel(rawStatus);
              const rideObj = req.rideId || {};
              const rideDate = rideObj.date ? parseISO(rideObj.date) : null;

              const c = req.customerId || {};
              const photoUrl = getFullPhotoUrl(c.profilePhotoUrl);

              return (
                <div
                  key={req._id}
                  className="relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6 rounded-3xl shadow-2xl border border-indigo-100 hover:shadow-blue-200 hover:scale-[1.02] transition"
                >
                  {getStatusBadge(status)}

                  {/* Passenger Info */}
                  <div className="flex items-center gap-4 mb-4">
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={c.name}
                        className="w-14 h-14 rounded-full border-2 border-blue-200 shadow"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white flex items-center justify-center font-bold text-lg">
                        {(c.name || "P")[0].toUpperCase()}
                      </div>
                    )}

                    <div>
                      <p className="font-bold text-gray-900 text-lg">
                        {c.name}
                      </p>
                      {req.message && (
                        <p className="italic text-gray-600">{req.message}</p>
                      )}
                      {rideDate && (
                        <p className="text-xs text-gray-500 mt-1">
                          Ride on {format(rideDate, "MMMM d, yyyy")} at{" "}
                          {rideObj.time || "TBD"}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Chat Button */}
                  <button
                    onClick={() =>
                      navigate(`/worker/ride-chat/${rideId}/${c._id}`)
                    }
                    className="absolute top-4 right-4 p-3 rounded-full bg-white shadow hover:bg-blue-50 transition"
                  >
                    <MessageCircle className="w-5 h-5 text-blue-600" />
                  </button>

                  {/* Buttons */}
                  <div className="flex flex-col gap-3 mt-3">
                    <div className="flex gap-3">
                      <button
                        onClick={() => openModal("accept", req)}
                        className="flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold shadow hover:scale-105"
                      >
                        <CheckCircle className="w-4 h-4" /> Accept
                      </button>
                      <button
                        onClick={() => openModal("decline", req)}
                        className="flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-red-500 to-pink-600 text-white font-semibold shadow hover:scale-105"
                      >
                        <XCircle className="w-4 h-4" /> Decline
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ================================
            CHATS
        ================================= */}
        <h3 className="text-2xl font-semibold mb-6">Interested Passengers</h3>
        <div className="grid sm:grid-cols-2 gap-8">
          {chats.map((c) => {
            const photoUrl = getFullPhotoUrl(c.profilePhotoUrl);
            const ts = new Date(c.timestamp || Date.now());

            return (
              <div
                key={c.customerId}
                onClick={() =>
                  navigate(`/worker/ride-chat/${rideId}/${c.customerId}`)
                }
                className="relative bg-gradient-to-br from-indigo-50 via-white to-blue-50 p-6 rounded-3xl shadow-2xl border hover:shadow-indigo-200 hover:scale-[1.02] transition cursor-pointer"
              >
                <div className="flex items-center gap-4 mb-4">
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt={c.name}
                      className="w-14 h-14 rounded-full border-2 border-indigo-200 shadow"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-indigo-500 text-white flex items-center justify-center font-bold text-lg shadow">
                      {(c.name || "P")[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="font-bold text-gray-900 text-lg">
                      {c.name}
                    </p>
                    <p className="text-xs text-gray-600">{c.lastMessage}</p>
                  </div>
                </div>

                <p className="text-xs text-gray-400 mb-6">
                  Last active {format(ts, "PPPpp")}
                </p>

                <MessageCircle className="absolute top-4 right-4 w-5 h-5 text-indigo-600" />
              </div>
            );
          })}
        </div>
      </div>

      {/* ================================
          UNIVERSAL MODAL
      ================================= */}
      {modal.open && modal.request && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h3 className="text-xl font-semibold mb-3 text-gray-900 text-center">
              {modal.type === "accept" && "Accept Ride Request"}
              {modal.type === "decline" && "Decline Ride Request"}
            </h3>

            <p className="text-sm text-gray-600 mb-6 text-center">
              {modal.type === "accept" &&
                "Are you sure you want to accept this request? Payment will be held in escrow."}
              {modal.type === "decline" &&
                "Are you sure you want to decline this request? Payment will be refunded instantly."}
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>

              {modal.type === "accept" && (
                <button
                  onClick={() => confirmAccept(modal.request)}
                  className="px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700"
                >
                  Confirm
                </button>
              )}

              {modal.type === "decline" && (
                <button
                  onClick={() => confirmDecline(modal.request)}
                  className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700"
                >
                  Confirm
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RideRequests;
